import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { useUser } from "@/contexts/UserContext";
import { billingService } from "@/services/billingService";
import { toast } from "sonner";

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

type PlanType = 'free' | 'starter' | 'professional' | 'enterprise';

interface CheckoutFormProps {
  planType: PlanType;
  planName: string;
  voucherCode?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CheckoutForm = ({
  planType,
  planName,
  voucherCode,
  onSuccess,
  onCancel,
}: CheckoutFormProps) => {
  const { user } = useUser();
  const [clientSecret, setClientSecret] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (user?.id && user?.email && planType) {
      createCheckoutSession();
    }
  }, [user, planType, voucherCode]);

  const createCheckoutSession = async () => {
    try {
      setLoading(true);
      setError("");

      // Get plans to find priceId for planType
      const plansResponse = await billingService.getPlans();
      const plans = plansResponse.data || plansResponse;
      const plan = plans.find((p: any) => p.id === planType || p.name?.toLowerCase() === planType.toLowerCase());
      
      if (!plan?.priceId) {
        throw new Error(`Plan ${planType} not found`);
      }

      const data = await billingService.createCheckoutSession({
        priceId: plan.priceId,
        voucherCode,
        successUrl: window.location.origin + '/dashboard',
        cancelUrl: window.location.origin + '/pricing',
      });

      if (data?.clientSecret || data?.data?.clientSecret) {
        setClientSecret(data.clientSecret || data.data.clientSecret);
      } else {
        throw new Error("No client secret received");
      }
    } catch (err: any) {
      console.error("Error creating checkout session:", err);
      setError(err.message || "Failed to create checkout session");
      toast.error(err.message || "Failed to create checkout session");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientSecret = async () => {
    // This function is required by EmbeddedCheckoutProvider
    // but we already have the client secret from our initial call
    return clientSecret;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">
            Setting up checkout for {planName}...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">
          <p className="font-medium">Checkout Error</p>
          <p className="text-sm">{error}</p>
        </div>
        <div className="space-x-4">
          <button
            onClick={createCheckoutSession}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Preparing checkout...</p>
      </div>
    );
  }

  // Check if Stripe is configured
  if (!stripePromise) {
    return (
      <div className="p-6 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-900 mb-2">
            Demo Mode - Payment Setup Required
          </h3>
          <p className="text-yellow-800 mb-4">
            Stripe is not configured in this environment. In production, you would complete payment here.
          </p>
          <div className="space-y-2">
            <p className="text-sm text-yellow-700">
              Selected Plan: <strong>{planName}</strong>
            </p>
            {voucherCode && (
              <p className="text-sm text-yellow-700">
                Voucher Code: <strong>{voucherCode}</strong>
              </p>
            )}
          </div>
          <div className="mt-6 space-x-4">
            <button
              onClick={onSuccess}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Continue (Demo)
            </button>
            {onCancel && (
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          fetchClientSecret,
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default CheckoutForm;